import { CANT_BOOK_STATUSES, deskDueBefore, type BookingWaitKind } from './booking-desk.js'
import { zonedDate, zonedMidnight } from './availability.js'
import { localTimeZone } from './booking-marks.js'

/**
 * The Booking desk as a picture of the whole pile (decision 114).
 *
 * Decision 106 put every job waiting to be booked into one of five states by
 * its next date. That answered "what do I do today" and nothing else: the
 * office could not see which jobs were quotes and which were said-yes work,
 * which customers come back every year, or keep its own lists. And the 1,638
 * imported quotes nobody had booked were not on the desk at all.
 *
 * Every unbooked job in the pipeline is now read three ways, each decided
 * here and nowhere else:
 *
 * - **Stage**, from its status and whether a quote went: New enquiry, Quote to
 *   send, Quote out, To book, On hold. Read left to right, it is the pipeline.
 * - **Customer**, from the client's history, never typed: Contract, Regular,
 *   Returning, New. "These guys do work with us every year" is a fact the
 *   database already holds.
 * - **Next step**, from the desk's own date (decision 106's rule without its
 *   fifth state, which is now the On hold stage): Due, Not looked at,
 *   Waiting on the customer, Later.
 *
 * Plus the office's own lists, which are the one judgement the data cannot
 * make: going ahead, chase up, unlikely.
 *
 * The filtering, counting and sorting below are pure, so the counts the
 * screen draws and the rows under them cannot disagree.
 */

/** Live, unbooked statuses on the desk: the pipeline from enquiry to on hold. */
export const PIPELINE_STATUSES = ['LEAD', 'QUOTING', 'QUOTED', 'APPROVED', 'AWAITING_PARTS', 'ON_HOLD'] as const

/* -------------------------------------------------------------------------- */
/* Stage                                                                       */
/* -------------------------------------------------------------------------- */

export const DESK_STAGES = ['enquiry', 'quoteToSend', 'quoteOut', 'toBook', 'onHold'] as const
export type DeskStage = (typeof DESK_STAGES)[number]

export const DESK_STAGE_LABEL: Record<DeskStage, string> = {
  enquiry: 'New enquiry',
  quoteToSend: 'Quote to send',
  quoteOut: 'Quote out',
  toBook: 'To book',
  onHold: 'On hold',
}

export const DESK_STAGE_CAPTION: Record<DeskStage, string> = {
  enquiry: 'Nothing quoted or booked yet',
  quoteToSend: 'Quote not sent yet',
  quoteOut: 'Sent, no yes yet',
  toBook: 'Said yes, needs a date',
  onHold: 'On hold or waiting on parts',
}

/** The quote stages, which "Going ahead" moves to To book. */
export const QUOTE_STAGES: readonly DeskStage[] = ['enquiry', 'quoteToSend', 'quoteOut']

export function deskStage(job: { status: string; quoteSentAt: Date | string | null }): DeskStage {
  if (CANT_BOOK_STATUSES.includes(job.status) && job.status !== 'QUOTED') return 'onHold'
  if (job.status === 'APPROVED') return 'toBook'
  // QUOTED is a quote that went, whether or not the roll-up has its date.
  if (job.status === 'QUOTED' || job.quoteSentAt) return 'quoteOut'
  if (job.status === 'LEAD') return 'enquiry'
  return 'quoteToSend'
}

/* -------------------------------------------------------------------------- */
/* Customer                                                                    */
/* -------------------------------------------------------------------------- */

export const CUSTOMER_KINDS = ['contract', 'regular', 'returning', 'new'] as const
export type CustomerKind = (typeof CUSTOMER_KINDS)[number]

/** Work done in this many different years makes a customer Regular. */
export const REGULAR_MIN_YEARS = 3

export const CUSTOMER_KIND_LABEL: Record<CustomerKind, string> = {
  contract: 'Contract',
  regular: 'Regular',
  returning: 'Returning',
  new: 'New',
}

export const CUSTOMER_KIND_CAPTION: Record<CustomerKind, string> = {
  contract: 'Agreement or repeat schedule',
  regular: `Work in ${REGULAR_MIN_YEARS} or more years`,
  returning: 'Work done before',
  new: 'No work done yet',
}

export interface CustomerHistory {
  /**
   * The job is a visit of a repeat schedule (ServiceM8's recurrence or ours),
   * or the client has a live agreement or schedule.
   */
  contract: boolean
  /** Different calendar years with completed work, this job aside. */
  yearsWorked: number
  /** Completed jobs, this one aside. */
  jobsDone: number
}

export function customerKind(history: CustomerHistory): CustomerKind {
  if (history.contract) return 'contract'
  if (history.yearsWorked >= REGULAR_MIN_YEARS) return 'regular'
  if (history.jobsDone > 0) return 'returning'
  return 'new'
}

/* -------------------------------------------------------------------------- */
/* Next step                                                                   */
/* -------------------------------------------------------------------------- */

export const DESK_NEXTS = ['due', 'notLookedAt', 'waiting', 'later'] as const
export type DeskNext = (typeof DESK_NEXTS)[number]

export const DESK_NEXT_LABEL: Record<DeskNext, string> = {
  due: 'Due',
  notLookedAt: 'Not looked at',
  waiting: 'Waiting on the customer',
  later: 'Later',
}

export const DESK_NEXT_CAPTION: Record<DeskNext, string> = {
  due: 'Its date is today or past',
  notLookedAt: 'Nobody has worked it yet',
  waiting: 'Followed up, no answer yet',
  later: '“Not until…”',
}

export function deskNext(
  job: { followUpAt: Date | null; followUpOn: BookingWaitKind | null },
  now: Date = new Date(),
  timeZone: string = localTimeZone(),
): DeskNext {
  if (!job.followUpAt) return 'notLookedAt'
  if (job.followUpAt < deskDueBefore(now, timeZone)) return 'due'
  return job.followUpOn === 'LATER' ? 'later' : 'waiting'
}

/** Due from before today: drawn red. */
export function deskLate(job: { followUpAt: Date | null }, now: Date = new Date(), timeZone: string = localTimeZone()): boolean {
  return job.followUpAt !== null && job.followUpAt < zonedMidnight(zonedDate(now, timeZone), timeZone)
}

/* -------------------------------------------------------------------------- */
/* Filtering, counting and sorting                                             */
/* -------------------------------------------------------------------------- */

/** One job on the desk, as the filters and sorts read it. */
export interface PipelineEntry {
  id: string
  stage: DeskStage
  customer: CustomerKind
  next: DeskNext
  late: boolean
  categoryId: string | null
  ownerId: string | null
  listIds: readonly string[]
  /** What its kind of work needs (decision 107), for the Needs filter. */
  capabilityIds: readonly string[]
  followUpAt: Date | null
  /** When it started waiting: the job, or the quote if that went first. */
  since: Date
  /** EMERGENCY 3, HIGH 2, NORMAL 1, LOW 0. */
  priorityRank: number
  /** The agreed price, else the job's total, in cents. */
  value: number
  yearsWorked: number
  jobsDone: number
  lastContactAt: Date | null
  clientName: string
}

export interface PipelineFilters {
  stage?: DeskStage | undefined
  customer?: CustomerKind | undefined
  next?: DeskNext | undefined
  listId?: string | undefined
  /** A kind of work, or `null` for jobs with none. */
  categoryId?: string | null | undefined
  /** A person, or `none` for jobs nobody owns. */
  ownerId?: string | undefined
  capabilityId?: string | undefined
}

type Facet = 'stage' | 'customer' | 'next' | 'list' | 'category' | 'owner' | 'capability'

function passes(entry: PipelineEntry, filters: PipelineFilters, skip: ReadonlySet<Facet>): boolean {
  if (!skip.has('stage') && filters.stage && entry.stage !== filters.stage) return false
  if (!skip.has('customer') && filters.customer && entry.customer !== filters.customer) return false
  if (!skip.has('next') && filters.next && entry.next !== filters.next) return false
  if (!skip.has('list') && filters.listId && !entry.listIds.includes(filters.listId)) return false
  if (!skip.has('category') && filters.categoryId !== undefined && entry.categoryId !== filters.categoryId) return false
  if (!skip.has('owner') && filters.ownerId) {
    if (filters.ownerId === 'none' ? entry.ownerId !== null : entry.ownerId !== filters.ownerId) return false
  }
  if (!skip.has('capability') && filters.capabilityId && !entry.capabilityIds.includes(filters.capabilityId)) return false
  return true
}

const NONE = new Set<Facet>()

export function pipelineFilter(entries: readonly PipelineEntry[], filters: PipelineFilters): PipelineEntry[] {
  return entries.filter((entry) => passes(entry, filters, NONE))
}

export interface PipelineCounts {
  /** Customer kind by stage, under every filter but those two. */
  matrix: Record<CustomerKind, Record<DeskStage, number>>
  /** Under every filter but the next step. */
  next: Record<DeskNext, number>
  /** Due from before today, under the same filters as `next`. */
  late: number
  /** Each list's jobs, under every filter but the list. */
  lists: Record<string, number>
  /** Each kind of work (`none` for none), under every filter but the kind. */
  categories: Record<string, number>
  /** Each owner (`none` for nobody), under every filter but the owner. */
  owners: Record<string, number>
  capabilities: Record<string, number>
  /** Everything on the desk under the filters that are not facets (search, area). */
  all: number
}

/**
 * The counts beside every filter. Each facet is counted under all the other
 * filters but not its own, so a count says what choosing it would show.
 */
export function pipelineCounts(entries: readonly PipelineEntry[], filters: PipelineFilters): PipelineCounts {
  const matrix = Object.fromEntries(
    CUSTOMER_KINDS.map((kind) => [kind, Object.fromEntries(DESK_STAGES.map((stage) => [stage, 0]))]),
  ) as PipelineCounts['matrix']
  const next = Object.fromEntries(DESK_NEXTS.map((key) => [key, 0])) as Record<DeskNext, number>
  const counts: PipelineCounts = { matrix, next, late: 0, lists: {}, categories: {}, owners: {}, capabilities: {}, all: entries.length }
  const bump = (bag: Record<string, number>, key: string) => {
    bag[key] = (bag[key] ?? 0) + 1
  }

  const skipMatrix = new Set<Facet>(['stage', 'customer'])
  const skipNext = new Set<Facet>(['next'])
  const skipList = new Set<Facet>(['list'])
  const skipCategory = new Set<Facet>(['category'])
  const skipOwner = new Set<Facet>(['owner'])
  const skipCapability = new Set<Facet>(['capability'])

  for (const entry of entries) {
    if (passes(entry, filters, skipMatrix)) matrix[entry.customer][entry.stage] += 1
    if (passes(entry, filters, skipNext)) {
      next[entry.next] += 1
      if (entry.next === 'due' && entry.late) counts.late += 1
    }
    if (passes(entry, filters, skipList)) for (const listId of entry.listIds) bump(counts.lists, listId)
    if (passes(entry, filters, skipCategory)) bump(counts.categories, entry.categoryId ?? 'none')
    if (passes(entry, filters, skipOwner)) bump(counts.owners, entry.ownerId ?? 'none')
    if (passes(entry, filters, skipCapability)) for (const id of entry.capabilityIds) bump(counts.capabilities, id)
  }
  return counts
}

export const DESK_SORTS = ['next', 'customer', 'value', 'oldest', 'lastContact', 'name'] as const
export type DeskSort = (typeof DESK_SORTS)[number]

export const DESK_SORT_LABEL: Record<DeskSort, string> = {
  next: 'Next step',
  customer: 'Best customers first',
  value: 'Highest value',
  oldest: 'Waiting longest',
  lastContact: 'Longest since contact',
  name: 'Client A–Z',
}

/** Due (latest first among the late), then not looked at, then waiting, then later. */
const NEXT_ORDER: Record<DeskNext, number> = { due: 0, notLookedAt: 1, waiting: 2, later: 3 }

function byNext(a: PipelineEntry, b: PipelineEntry): number {
  if (a.next !== b.next) return NEXT_ORDER[a.next] - NEXT_ORDER[b.next]
  if (a.next === 'notLookedAt') {
    // Nothing dated yet: the urgent, then the longest waiting.
    return b.priorityRank - a.priorityRank || a.since.getTime() - b.since.getTime()
  }
  return (a.followUpAt?.getTime() ?? 0) - (b.followUpAt?.getTime() ?? 0) || b.priorityRank - a.priorityRank
}

const byId = (a: PipelineEntry, b: PipelineEntry) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

export function pipelineSort(entries: readonly PipelineEntry[], sort: DeskSort): PipelineEntry[] {
  const compare: Record<DeskSort, (a: PipelineEntry, b: PipelineEntry) => number> = {
    next: byNext,
    customer: (a, b) => b.yearsWorked - a.yearsWorked || b.jobsDone - a.jobsDone || byNext(a, b),
    value: (a, b) => b.value - a.value || byNext(a, b),
    oldest: (a, b) => a.since.getTime() - b.since.getTime(),
    lastContact: (a, b) => {
      // Never tried comes first: it has waited longest for a word from us.
      if (!a.lastContactAt || !b.lastContactAt) return (a.lastContactAt ? 1 : 0) - (b.lastContactAt ? 1 : 0) || byNext(a, b)
      return a.lastContactAt.getTime() - b.lastContactAt.getTime()
    },
    name: (a, b) => a.clientName.localeCompare(b.clientName, 'en-AU', { sensitivity: 'base' }),
  }
  return [...entries].sort((a, b) => compare[sort](a, b) || byId(a, b))
}

/** How long a follow-up waits for an answer before the job is due again. */
export const FOLLOW_UP_BACK_IN_DAYS = 3

/** Names offered when the office has no lists yet. */
export const STARTER_DESK_LISTS = ['Going ahead', 'Chase up', 'Unlikely'] as const
